<template>
  <div
    class="py-4 px-4 custom-page"
    ref="screenshot-tab-wrapper"
  >
    <section>
      <b-row>
        <b-col>
          <div class="pb-5">
            <h1 class="page-title">
              Campaign Screenshots
            </h1>
          </div>
        </b-col>

        <b-col
          class="text-right"
        >
          <b-btn
            :disabled="screenshotNames.length < 1"
            variant="primary"
            class="ml-2 border-0"
            :class="{'btn-loading': isArchiveLoading}"
            data-test="download-archive-button"
            @click="downloadArchive"
          >
            <div class="archive-content-btn">
              <span v-if="!isArchiveLoading"> Download all screenshots as .zip &nbsp; <i class="fa-light fa-download" /></span>
            </div>
          </b-btn>
        </b-col>
      </b-row>
    </section>
    <b-row>
      <b-col
        class="col-lg-4 col-xl-4"
        v-if="GET_IS_MANAGED_ACCOUNT"
      >
        <div
          class="card pt-5 pb-6 add-screenshot"
          @mouseover="hoverAddScreenshot = true"
          @mouseleave="hoverAddScreenshot = false"
        >
          <template v-if="!hoverAddScreenshot && !displayUploadImageBlock">
            <div class="text-center">
              <i class="fa-light fa-circle-plus fa-4xl mb-6 mt-6" />
            </div>
            <div class="text-center">
              Add screenshot
            </div>
          </template>
          <template v-else>
            <div
              class="upload-image"
              @click="displayUploadImageBlock = true"
            >
              <p>Upload image file</p>
              <base-image-input
                v-model="screenshotImage"
                @input="addImageToList($event)"
                @uploadIsInvalid="uploadIsInvalid"
                @uploadIsStarted="uploadIsStarted"
                @uploadIsFinished="submitImages"
                @cancel="displayUploadImageBlock = false"
                ref="imageInput"
                :compressible="false"
                :multiple-upload="true"
                :disabled="isUploadInProgress"
              />
            </div>
          </template>
        </div>
      </b-col>
      <screenshot
        v-for="(name, index) in screenshotNames"
        :key="name"
        :name="name"
        :delete-allowed="GET_IS_MANAGED_ACCOUNT"
        :show-controls="screenshotNames.length > 1"
        :data-test="`screenshot-${index}`"
        :ref="`screenshot-${name}`"
        @updateCurrentScreenshot="updateCurrentScreenshot"
        @nextScreenshot="nextScreenshot"
        @prevScreenshot="prevScreenshot"
        @deleteScreenshot="deleteScreenshot"
      />
    </b-row>
  </div>
</template>
<script>
import {
  mapActions, mapGetters, mapMutations, mapState,
} from 'vuex';
import * as types from '@/store/types';
import * as reportingTypes from '@/modules/reporting/store/types';
import BaseImageInput from '@/components/BaseImageInput.vue';
import Screenshot from '@/modules/reporting/components/Screenshot.vue';
import fileHelper from '@/core/mixins/fileHelper';
import helper from '@/core/helper';
import * as accountTypes from '@/modules/account/store/types';

export default {
  name: 'CampaignScreenshots',
  components: { Screenshot, BaseImageInput },
  mixins: [
    fileHelper,
  ],
  data() {
    return {
      currentScreenshotName: '',
      displayUploadImageBlock: false,
      hoverAddScreenshot: false,
      isUploadInProgress: false,
      isArchiveLoading: false,
      screenshotImage: null,
      imageContent: [],
      screenshotNames: [],
      imagesPerRow: 3,
      initialImageAmount: 9,
      minScrollToNextBatch: 300,
      handleDebouncedWheel: null,
      images: [],
    };
  },
  computed: {
    ...mapState('reporting', [
      'campaignScreenshotNames',
    ]),
    ...mapState('campaigns', [
      'campaign',
    ]),
    ...mapGetters('account', [
      accountTypes.GET_IS_MANAGED_ACCOUNT,
    ]),
  },
  methods: {
    ...mapActions('reporting', [
      reportingTypes.UPLOAD_CAMPAIGN_SCREENSHOT,
      reportingTypes.FETCH_CAMPAIGN_SCREENSHOT_IMAGE,
      reportingTypes.FETCH_CAMPAIGN_SCREENSHOT_NAMES,
      reportingTypes.DOWNLOAD_SCREENSHOTS_ARCHIVE,
    ]),
    ...mapMutations([
      types.SET_GLOBAL_MESSAGE,
    ]),
    uploadIsInvalid(errorMessage) {
      this.displayMessage(errorMessage, 'danger');
    },
    uploadIsStarted() {
      this.displayMessage('Images being processed', 'info');
    },
    addImageToList(image) {
      this.images.push(image);
    },
    handleKeydown(e) {
      const keyCode = e.key;
      if (keyCode === 'ArrowLeft' || keyCode === 'a') {
        this.prevScreenshot();
      }
      if (e.key === 'ArrowRight' || keyCode === 'd') {
        this.nextScreenshot();
      }
    },
    submitImages() {
      this.isUploadInProgress = true;
      this.displayUploadImageBlock = false;
      Promise.all(this.images.map((image) => this.submitImage(image))).then(() => {
        this.displayMessage('Images have been uploaded', 'success');
        this.images = [];
        this.isUploadInProgress = false;
      }).catch(() => {
        this.images = [];
        this.isUploadInProgress = false;
      });
    },
    displayMessage(message, messageType) {
      this.SET_GLOBAL_MESSAGE({
        message,
        type: messageType,
      });
    },
    async submitImage(fileData) {
      const { formData } = await fileData;
      try {
        await this.UPLOAD_CAMPAIGN_SCREENSHOT(formData);
        this.screenshotNames.unshift(this.campaignScreenshotNames[0]);
      } finally {
        this.screenshotImage = null;
      }
    },
    async downloadArchive() {
      if (!this.isArchiveLoading) {
        this.isArchiveLoading = true;
        const archive = await this.DOWNLOAD_SCREENSHOTS_ARCHIVE();
        this.downloadZip(archive, `${this.campaign.name} - Screenshots.zip`);
        this.isArchiveLoading = false;
      }
    },
    uploadNextBatch() {
      const currentImagesAmount = this.screenshotNames.length;
      if (currentImagesAmount < this.campaignScreenshotNames.length) {
        const scrollPosition = this.$refs['screenshot-tab-wrapper']?.getBoundingClientRect().top * -1;

        if (scrollPosition > 0) {
          const currentRowsAmount = Math.ceil(currentImagesAmount / this.imagesPerRow);
          const expectedRowsAmount = Math.ceil(scrollPosition / this.minScrollToNextBatch) + 2;
          if (currentRowsAmount < expectedRowsAmount + 1) {
            this.screenshotNames = this.screenshotNames.concat(this.campaignScreenshotNames
              .slice(currentImagesAmount, currentImagesAmount + this.imagesPerRow));
          }
        }
      }
    },
    nextScreenshot() {
      const currentIndex = this.screenshotNames.indexOf(this.currentScreenshotName);
      if (currentIndex > -1) {
        const nextIndex = currentIndex + 1 < this.screenshotNames.length ? currentIndex + 1 : 0;
        const nextScreenshotName = this.screenshotNames[nextIndex];
        this.$refs[`screenshot-${this.currentScreenshotName}`][0].closeModal();
        this.$refs[`screenshot-${nextScreenshotName}`][0].openModal();
      }
    },
    prevScreenshot() {
      const currentIndex = this.screenshotNames.indexOf(this.currentScreenshotName);
      if (currentIndex > -1) {
        const prevIndex = currentIndex !== 0 ? currentIndex - 1 : this.screenshotNames.length - 1;
        const prevScreenshotName = this.screenshotNames[prevIndex];
        this.$refs[`screenshot-${this.currentScreenshotName}`][0].closeModal();
        this.$refs[`screenshot-${prevScreenshotName}`][0].openModal();
      }
    },
    updateCurrentScreenshot(imageName) {
      this.currentScreenshotName = imageName;
    },
    deleteScreenshot() {
      this.screenshotNames = this.campaignScreenshotNames.slice(0, this.screenshotNames.length);
    },
  },
  created() {
    const screenshotNamesLength = this.campaignScreenshotNames.length;
    if (this.GET_IS_MANAGED_ACCOUNT) this.initialImageAmount++;

    if (screenshotNamesLength > this.initialImageAmount) {
      this.screenshotNames = this.campaignScreenshotNames.slice(0, this.initialImageAmount);
      this.handleDebouncedWheel = helper.debounce(this.uploadNextBatch, 500);
      window.addEventListener('wheel', this.handleDebouncedWheel);
    } else {
      this.screenshotNames = this.campaignScreenshotNames;
    }
  },
  mounted() {
    if (!process.server) {
      window.addEventListener('keydown', this.handleKeydown);
    }
  },
  beforeDestroy() {
    if (!process.server) {
      window.removeEventListener('keydown', this.handleKeydown);
    }
    window.removeEventListener('wheel', this.handleDebouncedWheel);
  },
};
</script>
<style lang="scss" scoped>
.archive-content-btn {
  width: 225px;
  height: 26px;
}
.custom-page {
  min-height: 100%;
}
.add-screenshot {
  min-height: 175px;
  justify-content: center;
}
.upload-image {
  width: 70%;
  margin: 0 auto;
}
</style>
